import type {
  EmployeePayrollCalculationResultDto,
  PayrollGrossLineDto,
  PayrollRun,
  PayrollRunPreviewDto,
  PayrollRunPreviewFiltersSnapshot,
} from '../types';
import { formatCompanyDate } from './companyDateFormat';

export type PayrollReportLayout = 'detail' | 'summary';
export type PayrollLineBreakdown = 'periodTotal' | 'scheduleBreakdown' | 'scheduleDayTypeBreakdown';

export interface PayrollReportOutputFieldDef {
  key: string;
  defaultLabel: string;
  layouts: PayrollReportLayout[];
}

export interface PayrollReportFilters {
  payrollRunId: number | null;
  onlyWarnings: boolean;
  onlyErrors: boolean;
  lineCategories: string[];
  /** Period-level totals vs schedule-level breakdown (detail layout). */
  lineBreakdown: PayrollLineBreakdown;
  /** When true, employee name is shown once per employee block; when false, on every row. */
  groupByEmployee: boolean;
}

export const DEFAULT_PAYROLL_DETAIL_OUTPUT_KEYS = [
  'payPeriodType',
  'employeeName',
  'lineCategory',
  'lineHours',
  'lineRate',
  'lineAmount',
  'lineDetail',
];

export const DEFAULT_PAYROLL_SUMMARY_OUTPUT_KEYS = [
  'payPeriodType',
  'employeeName',
  'grossTotal',
  'derivedHourlyBase',
  'totalOrdinaryHours',
  'totalPremiumHours',
  'totalHolidayHours',
  'totalOtHours',
  'warningCount',
  'errorCount',
];

export const PAYROLL_REPORT_OUTPUT_FIELD_CATALOG: PayrollReportOutputFieldDef[] = [
  { key: 'employeeId', defaultLabel: 'Employee ID', layouts: ['detail', 'summary'] },
  { key: 'employeeName', defaultLabel: 'Employee', layouts: ['detail', 'summary'] },
  { key: 'firstName', defaultLabel: 'First name', layouts: ['detail', 'summary'] },
  { key: 'lastName', defaultLabel: 'Last name', layouts: ['detail', 'summary'] },
  { key: 'worksNumber', defaultLabel: 'Works number', layouts: ['detail', 'summary'] },
  { key: 'payPeriodType', defaultLabel: 'Pay period', layouts: ['detail', 'summary'] },
  { key: 'grossTotal', defaultLabel: 'Total gross', layouts: ['summary'] },
  { key: 'derivedHourlyBase', defaultLabel: 'Hourly base', layouts: ['summary'] },
  { key: 'totalOrdinaryHours', defaultLabel: 'Ordinary hours', layouts: ['summary'] },
  { key: 'totalPremiumHours', defaultLabel: 'Premium hours', layouts: ['summary'] },
  { key: 'totalHolidayHours', defaultLabel: 'Holiday hours', layouts: ['summary'] },
  { key: 'totalOtHours', defaultLabel: 'OT hours', layouts: ['summary'] },
  { key: 'totalHours', defaultLabel: 'Total hours', layouts: ['summary'] },
  { key: 'warningCount', defaultLabel: 'Warnings', layouts: ['summary'] },
  { key: 'errorCount', defaultLabel: 'Errors', layouts: ['summary'] },
  { key: 'warningsSummary', defaultLabel: 'Warning messages', layouts: ['summary'] },
  { key: 'errorsSummary', defaultLabel: 'Error messages', layouts: ['summary'] },
  { key: 'lineDate', defaultLabel: 'Date', layouts: ['detail'] },
  { key: 'lineCategory', defaultLabel: 'Category', layouts: ['detail'] },
  { key: 'lineHours', defaultLabel: 'Hours', layouts: ['detail'] },
  { key: 'lineRate', defaultLabel: 'Rate applied', layouts: ['detail'] },
  { key: 'lineMultiplier', defaultLabel: 'Multiplier', layouts: ['detail'] },
  { key: 'lineAmount', defaultLabel: 'Amount', layouts: ['detail'] },
  { key: 'lineDetail', defaultLabel: 'Detail / schedule', layouts: ['detail'] },
  { key: 'runPeriodStart', defaultLabel: 'Period start', layouts: ['detail', 'summary'] },
  { key: 'runPeriodEnd', defaultLabel: 'Period end', layouts: ['detail', 'summary'] },
  { key: 'runStatus', defaultLabel: 'Run status', layouts: ['detail', 'summary'] },
];

/** Detail row sort: pay matrix → nurse banding worked → holiday pay → premiums → extras. */
export const PAYROLL_DETAIL_LINE_ORDER: Record<string, number> = {
  MatrixRate: 0,
  NurseBandWorked: 1,
  PaidLeave: 2,
  SundayPremium: 3,
  BankHolidayPay: 4,
  NurseBandBhStatutory: 5,
  TimecardExtras: 6,
  NurseBandAlAccrual: 7,
};

const EXCLUDED_LINE_CATEGORIES = new Set(['WeekSubtotal', 'NurseBandAlAccrual']);

function reportablePayrollLines(lines: PayrollGrossLineDto[]): PayrollGrossLineDto[] {
  return lines.filter((l) => !EXCLUDED_LINE_CATEGORIES.has(l.category));
}

export function getDefaultLabelForPayrollOutputField(fieldKey: string): string {
  return PAYROLL_REPORT_OUTPUT_FIELD_CATALOG.find((f) => f.key === fieldKey)?.defaultLabel || fieldKey;
}

export function catalogFieldsForLayout(layout: PayrollReportLayout): PayrollReportOutputFieldDef[] {
  return PAYROLL_REPORT_OUTPUT_FIELD_CATALOG.filter((f) => f.layouts.includes(layout));
}

export function defaultOutputKeysForLayout(layout: PayrollReportLayout): string[] {
  return layout === 'summary' ? DEFAULT_PAYROLL_SUMMARY_OUTPUT_KEYS : DEFAULT_PAYROLL_DETAIL_OUTPUT_KEYS;
}

export interface PayrollReportRowContext {
  preview: PayrollRunPreviewDto;
  runStatus?: string | null;
  worksNumberByEmployeeId?: Record<number, string>;
  firstNameByEmployeeId?: Record<number, string>;
  lastNameByEmployeeId?: Record<number, string>;
  payPeriodType?: string | null;
  payPeriodLabel?: string | null;
  groupByEmployee?: boolean;
  /** Previous row in export/preview — used to suppress repeated employee names when grouped. */
  previousEmployeeId?: number | null;
}

export interface PayrollDetailReportRow {
  layout: 'detail';
  employee: EmployeePayrollCalculationResultDto;
  line: PayrollGrossLineDto;
}

export interface PayrollSummaryReportRow {
  layout: 'summary';
  employee: EmployeePayrollCalculationResultDto;
}

export type PayrollReportDataRow = PayrollDetailReportRow | PayrollSummaryReportRow;

function formatNum(v: number | null | undefined, digits = 2): string {
  if (v == null || Number.isNaN(v)) return '';
  return Number(v).toFixed(digits);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return formatCompanyDate(d);
}

function isOrdinaryCategory(category: string): boolean {
  const c = category.toLowerCase();
  return c.includes('ordinary') || c.includes('base') || c === 'salary' || c === 'nursebandworked';
}

function isPremiumCategory(category: string): boolean {
  const c = category.toLowerCase();
  return c.includes('sunday') || c.includes('night') || (c.includes('premium') && !c.includes('holiday'));
}

function isHolidayCategory(category: string): boolean {
  const c = category.toLowerCase();
  return c.includes('holiday') || c.includes('ph ') || c.startsWith('ph') || c.includes('public holiday')
    || c === 'nursebandbhstatutory';
}

function isOtCategory(category: string): boolean {
  const c = category.toLowerCase();
  return c.includes('ot') || c.includes('overtime');
}

function sumHours(lines: PayrollGrossLineDto[], predicate: (c: string) => boolean): number {
  return lines.filter((l) => predicate(l.category)).reduce((acc, l) => acc + (l.hours || 0), 0);
}

function lineAmount(ln: PayrollGrossLineDto): number {
  if (ln.category === 'MatrixRate' && Number(ln.amount) === 0) {
    return Number(ln.hours) * Number(ln.hourlyRateApplied);
  }
  return Number(ln.amount);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function parsePayrollRunFiltersSnapshot(run: PayrollRun | null | undefined): PayrollRunPreviewFiltersSnapshot | null {
  if (!run?.previewFiltersJson) return null;
  try {
    const o = JSON.parse(run.previewFiltersJson) as unknown;
    if (o && typeof o === 'object' && o !== null && 'freqKind' in o) {
      return o as PayrollRunPreviewFiltersSnapshot;
    }
  } catch {
    return null;
  }
  return null;
}

/** Hourly / Salaried (or both) from the run’s saved preview filters. */
export function resolvePayStaffingKind(run: PayrollRun | null | undefined): string | null {
  const snap = parsePayrollRunFiltersSnapshot(run);
  if (!snap) return null;
  if (snap.payCategoryFilter === 'hourly') return 'Hourly';
  if (snap.payCategoryFilter === 'salaried') return 'Salaried';
  if (snap.payCategoryFilter === 'both') return 'Hourly & Salaried';
  if (snap.freqKind === 'ooh-hourly') return 'Hourly';
  if (snap.freqKind === 'monthly') return 'Salaried';
  return null;
}

export function resolvePayPeriodType(run: PayrollRun | null | undefined): string {
  const snap = parsePayrollRunFiltersSnapshot(run);
  let cycle: string;
  if (snap?.freqKind === 'ooh-hourly') cycle = 'Hourly 29–28';
  else if (snap?.freqKind === 'monthly') cycle = 'Monthly';
  else if (snap?.freqKind === 'fortnightly') cycle = 'Bi-Weekly';
  else if (snap?.freqKind === 'weekly') cycle = 'Weekly';
  else {
    const days =
      run?.periodStart && run?.periodEnd
        ? Math.round(
            (new Date(run.periodEnd).getTime() - new Date(run.periodStart).getTime()) / (24 * 60 * 60 * 1000)
          ) + 1
        : 0;
    if (days >= 28) cycle = 'Monthly';
    else if (days >= 13) cycle = 'Bi-Weekly';
    else if (days >= 6) cycle = 'Weekly';
    else cycle = 'Pay period';
  }

  const staffing = resolvePayStaffingKind(run);
  if (!staffing) return cycle;
  // Avoid "Hourly · Hourly 29–28"
  if (cycle.toLowerCase().startsWith(staffing.toLowerCase())) return cycle;
  return `${staffing} · ${cycle}`;
}

export function resolvePayPeriodLabel(run: PayrollRun | null | undefined): string {
  const type = resolvePayPeriodType(run);
  const start = run?.periodStart ? formatDate(run.periodStart) : '';
  const end = run?.periodEnd ? formatDate(run.periodEnd) : '';
  if (start && end) return `${type}: ${start} – ${end}`;
  return type;
}

/** Compact label for run lists: "Hourly · 2026-06-29 → 2026-07-28". */
export function formatPayrollRunPeriodRow(run: PayrollRun | null | undefined): string {
  if (!run) return '—';
  const start = run.periodStart?.slice(0, 10) ?? '';
  const end = run.periodEnd?.slice(0, 10) ?? '';
  const range = start && end ? `${start} → ${end}` : start || end || '—';
  const staffing = resolvePayStaffingKind(run);
  const snap = parsePayrollRunFiltersSnapshot(run);
  const cycleHint =
    snap?.freqKind === 'ooh-hourly'
      ? 'Hourly 29–28'
      : snap?.freqKind === 'monthly'
        ? 'Monthly'
        : snap?.freqKind === 'fortnightly'
          ? 'Bi-Weekly'
          : snap?.freqKind === 'weekly'
            ? 'Weekly'
            : null;
  const head = staffing ?? cycleHint;
  return head ? `${head} · ${range}` : range;
}

export function displayLineCategory(category: string): string {
  if (category === 'MatrixRate') return 'Pay matrix';
  if (category === 'NurseBandWorked') return 'Nurse banding worked';
  if (category === 'NurseBandBhStatutory') return 'Bank holiday statutory';
  if (category === 'NurseBandAlAccrual') return 'Annual leave accrual';
  if (category === 'PaidLeave') return 'Holiday pay';
  if (category === 'SundayPremium') return 'Sunday premium';
  if (category === 'BankHolidayPay') return 'Holiday premium';
  if (category === 'TimecardExtras') return 'Extras';
  if (category === 'WeekSubtotal') return 'Week subtotal';
  if (category === 'Salary') return 'Salary';
  return category;
}

function extractScheduleLabel(detail: string | null | undefined): string {
  if (!detail) return 'Default schedule';
  const idx = detail.indexOf(' — ');
  return idx > 0 ? detail.slice(0, idx).trim() : detail.trim() || 'Default schedule';
}

/** Nurse banding detail: "{position} — {schedule} — Year N — {day type}: base €…" (legacy: schedule first). */
function parseNurseBandPayrollDetail(detail: string | null | undefined): {
  schedule: string;
  dayType: string | null;
} | null {
  const d = (detail || '').trim();
  if (!d) return null;

  const yearTokenMatch = d.match(/ — (Year \d+|Any year) — /);
  if (!yearTokenMatch || yearTokenMatch.index == null) return null;

  const beforeYear = d.slice(0, yearTokenMatch.index).trim();
  const afterYear = d.slice(yearTokenMatch.index + yearTokenMatch[0].length);

  const segmentsBeforeYear = beforeYear.split(' — ').map((p) => p.trim()).filter(Boolean);
  const schedule =
    segmentsBeforeYear.length >= 2
      ? segmentsBeforeYear[segmentsBeforeYear.length - 1]!
      : segmentsBeforeYear[0] ?? 'Default schedule';

  const dayTypeMatch = afterYear.match(/^([^:]+):/);
  const dayType = dayTypeMatch?.[1]?.trim() ?? null;

  return { schedule, dayType };
}

function extractNurseBandScheduleLabel(detail: string | null | undefined): string {
  const parsed = parseNurseBandPayrollDetail(detail);
  if (parsed?.schedule) return parsed.schedule;
  return extractScheduleLabel(detail);
}

/** Nurse banding schedule + day type label, including red-eye segments. */
function extractNurseBandScheduleDayTypeLabel(detail: string | null | undefined): string {
  const parsed = parseNurseBandPayrollDetail(detail);
  if (parsed?.dayType) return `${parsed.schedule} — ${parsed.dayType}`;
  if (parsed?.schedule) return parsed.schedule;
  return extractScheduleLabel(detail);
}

export function normalizePayrollLineBreakdown(value: string | undefined | null): PayrollLineBreakdown {
  if (value === 'scheduleDayTypeBreakdown') return 'scheduleDayTypeBreakdown';
  if (value === 'scheduleBreakdown') return 'scheduleBreakdown';
  return 'periodTotal';
}

function isCategorySummaryLabel(text: string): boolean {
  const t = text.replace(/\s+total$/i, '').trim().toLowerCase();
  return (
    t === 'pay matrix' ||
    t === 'nurse banding worked' ||
    t === 'bank holiday statutory' ||
    t === 'annual leave accrual' ||
    t === 'holiday pay' ||
    t === 'sunday premium' ||
    t === 'holiday premium' ||
    t === 'extras'
  );
}

function stripPeriodTotalSuffix(name: string): string {
  if (name.endsWith(' total')) return name.slice(0, -6).trim();
  return name;
}

function matrixScheduleNameFromDetail(
  detail: string | null | undefined,
  periodLabel?: string | null
): string {
  const d = (detail || '').trim();
  if (!d) return '';

  if (periodLabel && d.startsWith(`${periodLabel} — `)) {
    const name = d.slice(periodLabel.length + 3).trim();
    if (name.endsWith(' total')) return stripPeriodTotalSuffix(name);
    if (isCategorySummaryLabel(name)) return name;
    return name;
  }

  const rawMatch = d.match(/^(.+?) — (?:pay matrix rate|default rate)/);
  if (rawMatch) return rawMatch[1].trim();

  const parts = d.split(' — ').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const last = parts[parts.length - 1];
    if (!last.endsWith(' total') && !isCategorySummaryLabel(last) && !/^\d/.test(last) && !last.includes('× €')) {
      return last;
    }
  }

  const first = parts[0] || '';
  if (/^(Weekly|Monthly|Bi-Weekly):/.test(first)) return '';
  if (isCategorySummaryLabel(first)) return '';
  return first;
}

/** Detail / schedule column: schedule name or fixed premium labels only. */
export function formatPayrollLineDetail(
  line: PayrollGrossLineDto,
  periodLabel?: string | null
): string {
  const category = line.category || '';
  if (category === 'SundayPremium') return 'Sunday Premiums';
  if (category === 'BankHolidayPay') return 'BH pay premiums';
  if (category === 'PaidLeave') return 'Paid holiday Pay';
  if (category === 'TimecardExtras') return 'Extras';
  if (category === 'NurseBandWorked') {
    if (periodLabel && line.detail?.startsWith(`${periodLabel} — `)) {
      return matrixScheduleNameFromDetail(line.detail, periodLabel);
    }
    if (line.detail?.includes(' — Year ') || line.detail?.includes(' — Any year ')) {
      return extractNurseBandScheduleDayTypeLabel(line.detail);
    }
    return extractNurseBandScheduleLabel(line.detail);
  }
  if (category === 'NurseBandBhStatutory') return 'Bank holiday statutory';
  if (category === 'NurseBandAlAccrual') return 'Annual leave accrual';

  return matrixScheduleNameFromDetail(line.detail, periodLabel);
}

function buildSyntheticLine(
  category: string,
  hours: number,
  amount: number,
  detail: string,
  rate?: number
): PayrollGrossLineDto {
  const r = rate ?? (hours > 0 ? amount / hours : 0);
  return {
    category,
    date: undefined,
    hours: round2(hours),
    hourlyRateApplied: round2(r),
    multiplier: 1,
    amount: round2(amount),
    detail,
  };
}

function aggregateCategoryTotal(
  lines: PayrollGrossLineDto[],
  category: string,
  periodLabel: string
): PayrollGrossLineDto | null {
  const group = lines.filter((l) => l.category === category);
  if (group.length === 0) return null;
  const hours = group.reduce((s, l) => s + Number(l.hours), 0);
  const amount = group.reduce((s, l) => s + lineAmount(l), 0);
  if (category === 'TimecardExtras' && amount <= 0) return null;
  const rates = group.map((l) => Number(l.hourlyRateApplied)).filter((r) => r > 0);
  let rate: number | undefined;
  if (amount > 0 && hours > 0) {
    rate = rates.length === 1 ? rates[0] : amount / hours;
  } else if (rates.length === 1) {
    rate = rates[0];
  } else if (rates.length > 1) {
    rate = rates.reduce((a, b) => a + b, 0) / rates.length;
  }
  return buildSyntheticLine(
    category,
    hours,
    amount,
    `${periodLabel} — ${displayLineCategory(category)} total`,
    rate
  );
}

function aggregatePeriodTotals(
  lines: PayrollGrossLineDto[],
  periodLabel: string
): PayrollGrossLineDto[] {
  const usable = reportablePayrollLines(lines);
  const categories = Array.from(new Set(usable.map((l) => l.category)));
  const result: PayrollGrossLineDto[] = [];

  for (const cat of categories.sort((a, b) => {
    const oa = PAYROLL_DETAIL_LINE_ORDER[a] ?? 50;
    const ob = PAYROLL_DETAIL_LINE_ORDER[b] ?? 50;
    return oa - ob || a.localeCompare(b);
  })) {
    const row = aggregateCategoryTotal(usable, cat, periodLabel);
    if (row) result.push(row);
  }
  return result;
}

function aggregateScheduleBreakdown(
  lines: PayrollGrossLineDto[],
  periodLabel: string,
  breakdown: 'scheduleBreakdown' | 'scheduleDayTypeBreakdown'
): PayrollGrossLineDto[] {
  const usable = reportablePayrollLines(lines);
  const result: PayrollGrossLineDto[] = [];

  const pushScheduleBreakdown = (
    lines: PayrollGrossLineDto[],
    category: string,
    scheduleKey: (ln: PayrollGrossLineDto) => string
  ) => {
    const bySchedule = new Map<string, PayrollGrossLineDto[]>();
    for (const ln of lines) {
      const key = scheduleKey(ln);
      const arr = bySchedule.get(key) ?? [];
      arr.push(ln);
      bySchedule.set(key, arr);
    }
    const scheduleEntries = Array.from(bySchedule.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    for (const [schedule, group] of scheduleEntries) {
      const hours = group.reduce((s: number, l: PayrollGrossLineDto) => s + Number(l.hours), 0);
      const amount = group.reduce((s: number, l: PayrollGrossLineDto) => s + lineAmount(l), 0);
      const rates = group.map((l: PayrollGrossLineDto) => Number(l.hourlyRateApplied)).filter((r: number) => r > 0);
      const rate = rates.length === 1 ? rates[0] : hours > 0 ? amount / hours : 0;
      result.push(
        buildSyntheticLine(category, hours, amount, `${periodLabel} — ${schedule}`, rate)
      );
    }
  };

  pushScheduleBreakdown(
    usable.filter((l) => l.category === 'MatrixRate'),
    'MatrixRate',
    (ln) => matrixScheduleNameFromDetail(ln.detail) || extractScheduleLabel(ln.detail)
  );

  pushScheduleBreakdown(
    usable.filter((l) => l.category === 'NurseBandWorked'),
    'NurseBandWorked',
    breakdown === 'scheduleDayTypeBreakdown'
      ? (ln) => extractNurseBandScheduleDayTypeLabel(ln.detail)
      : (ln) => extractNurseBandScheduleLabel(ln.detail)
  );

  const paidLeave = aggregateCategoryTotal(usable, 'PaidLeave', periodLabel);
  if (paidLeave) result.push(paidLeave);

  const sunday = aggregateCategoryTotal(usable, 'SundayPremium', periodLabel);
  if (sunday) result.push(sunday);

  const holidayPremium = aggregateCategoryTotal(usable, 'BankHolidayPay', periodLabel);
  if (holidayPremium) result.push(holidayPremium);

  const extras = aggregateCategoryTotal(usable, 'TimecardExtras', periodLabel);
  if (extras && Number(extras.amount) > 0) result.push(extras);

  const handled = new Set([
    'MatrixRate',
    'NurseBandWorked',
    'PaidLeave',
    'SundayPremium',
    'BankHolidayPay',
    'TimecardExtras',
  ]);
  const otherCats = Array.from(new Set(usable.map((l) => l.category))).filter((c) => !handled.has(c));
  for (const cat of otherCats.sort()) {
    const row = aggregateCategoryTotal(usable, cat, periodLabel);
    if (row) result.push(row);
  }

  return result;
}

function transformEmployeeLines(
  lines: PayrollGrossLineDto[],
  breakdown: PayrollLineBreakdown,
  periodLabel: string
): PayrollGrossLineDto[] {
  if (breakdown === 'scheduleBreakdown' || breakdown === 'scheduleDayTypeBreakdown') {
    return aggregateScheduleBreakdown(lines, periodLabel, breakdown);
  }
  return aggregatePeriodTotals(lines, periodLabel);
}

function compareDetailRows(a: PayrollDetailReportRow, b: PayrollDetailReportRow): number {
  const emp = a.employee.employeeName.localeCompare(b.employee.employeeName, undefined, {
    sensitivity: 'base',
  });
  if (emp !== 0) return emp;

  const oa = PAYROLL_DETAIL_LINE_ORDER[a.line.category] ?? 50;
  const ob = PAYROLL_DETAIL_LINE_ORDER[b.line.category] ?? 50;
  if (oa !== ob) return oa - ob;

  return (a.line.detail || '').localeCompare(b.line.detail || '', undefined, { sensitivity: 'base' });
}

export function buildPayrollReportRows(
  preview: PayrollRunPreviewDto,
  layout: PayrollReportLayout,
  filters: PayrollReportFilters,
  run?: PayrollRun | null
): PayrollReportDataRow[] {
  let employees = [...preview.employees];

  if (filters.onlyWarnings) {
    employees = employees.filter((e) => (e.warnings?.length ?? 0) > 0);
  }
  if (filters.onlyErrors) {
    employees = employees.filter((e) => (e.errors?.length ?? 0) > 0 || !e.success);
  }

  employees.sort((a, b) => {
    const aPaid = a.grossTotal > 0 ? 1 : 0;
    const bPaid = b.grossTotal > 0 ? 1 : 0;
    if (bPaid !== aPaid) return bPaid - aPaid;
    return a.employeeName.localeCompare(b.employeeName, undefined, { sensitivity: 'base' });
  });

  if (layout === 'summary') {
    return employees.map((employee) => ({ layout: 'summary', employee }));
  }

  const periodLabel = resolvePayPeriodLabel(run ?? null);
  const rows: PayrollDetailReportRow[] = [];
  const categoryFilter = new Set(
    (filters.lineCategories || []).map((c) => c.trim().toLowerCase()).filter(Boolean)
  );

  for (const employee of employees) {
    const transformed = transformEmployeeLines(
      employee.lines || [],
      filters.lineBreakdown,
      periodLabel
    );
    for (const line of transformed) {
      if (categoryFilter.size > 0 && !categoryFilter.has(line.category.trim().toLowerCase())) {
        continue;
      }
      rows.push({ layout: 'detail', employee, line });
    }
  }

  rows.sort(compareDetailRows);
  return rows;
}

function formatGroupedEmployeeText(
  employeeId: number,
  value: string,
  context: PayrollReportRowContext,
  layout: PayrollReportLayout
): string {
  if (layout === 'detail' && context.groupByEmployee && context.previousEmployeeId === employeeId) {
    return '';
  }
  return value;
}

export function formatPayrollReportCell(
  row: PayrollReportDataRow,
  fieldKey: string,
  context: PayrollReportRowContext
): string {
  const trimmedKey = String(fieldKey || '').trim();
  const key =
    PAYROLL_REPORT_OUTPUT_FIELD_CATALOG.find((f) => f.key.toLowerCase() === trimmedKey.toLowerCase())?.key ??
    trimmedKey;

  const employee = row.employee;
  const worksNumber = context.worksNumberByEmployeeId?.[employee.employeeId] ?? '';
  const firstName = context.firstNameByEmployeeId?.[employee.employeeId] ?? '';
  const lastName = context.lastNameByEmployeeId?.[employee.employeeId] ?? '';

  if (key === 'runPeriodStart') return formatDate(context.preview.periodStart);
  if (key === 'runPeriodEnd') return formatDate(context.preview.periodEnd);
  if (key === 'runStatus') return context.runStatus ?? '';
  if (key === 'payPeriodType') return context.payPeriodType ?? '';

  if (row.layout === 'summary') {
    const lines = reportablePayrollLines(employee.lines || []);
    switch (key) {
      case 'employeeId':
        return String(employee.employeeId);
      case 'employeeName':
        return employee.employeeName || '';
      case 'firstName':
        return firstName;
      case 'lastName':
        return lastName;
      case 'worksNumber':
        return worksNumber;
      case 'grossTotal':
        return formatNum(employee.grossTotal);
      case 'derivedHourlyBase':
        return formatNum(employee.derivedHourlyBase);
      case 'totalOrdinaryHours':
        return formatNum(sumHours(lines, isOrdinaryCategory));
      case 'totalPremiumHours':
        return formatNum(sumHours(lines, isPremiumCategory));
      case 'totalHolidayHours':
        return formatNum(sumHours(lines, isHolidayCategory));
      case 'totalOtHours':
        return formatNum(sumHours(lines, isOtCategory));
      case 'totalHours':
        return formatNum(lines.reduce((acc, l) => acc + (l.hours || 0), 0));
      case 'warningCount':
        return String(employee.warnings?.length ?? 0);
      case 'errorCount':
        return String(employee.errors?.length ?? 0);
      case 'warningsSummary':
        return (employee.warnings || []).join('; ');
      case 'errorsSummary':
        return (employee.errors || []).join('; ');
      default:
        return '';
    }
  }

  const line = row.line;
  switch (key) {
    case 'employeeId':
      return String(employee.employeeId);
    case 'employeeName':
      return formatGroupedEmployeeText(
        employee.employeeId,
        employee.employeeName || '',
        context,
        row.layout
      );
    case 'firstName':
      return formatGroupedEmployeeText(employee.employeeId, firstName, context, row.layout);
    case 'lastName':
      return formatGroupedEmployeeText(employee.employeeId, lastName, context, row.layout);
    case 'worksNumber':
      return worksNumber;
    case 'lineDate':
      return line.date ? formatDate(line.date) : context.payPeriodLabel?.replace(/^[^:]+:\s*/, '') ?? '';
    case 'lineCategory':
      return displayLineCategory(line.category || '');
    case 'lineHours':
      return formatNum(line.hours);
    case 'lineRate':
      if (line.category === 'WeekSubtotal') return '';
      return formatNum(line.hourlyRateApplied);
    case 'lineMultiplier':
      return formatNum(line.multiplier);
    case 'lineAmount':
      return formatNum(lineAmount(line));
    case 'lineDetail':
      return formatPayrollLineDetail(line, context.payPeriodLabel);
    default:
      return '';
  }
}

export function defaultPayrollFilters(): PayrollReportFilters {
  return {
    payrollRunId: null,
    onlyWarnings: false,
    onlyErrors: false,
    lineCategories: [],
    lineBreakdown: 'periodTotal',
    groupByEmployee: true,
  };
}

export function parsePayrollFiltersFromJson(json: string): PayrollReportFilters {
  try {
    const o = JSON.parse(json) as Partial<PayrollReportFilters>;
    return {
      payrollRunId: o.payrollRunId ?? null,
      onlyWarnings: Boolean(o.onlyWarnings),
      onlyErrors: Boolean(o.onlyErrors),
      lineCategories: Array.isArray(o.lineCategories) ? o.lineCategories.map(String) : [],
      lineBreakdown: normalizePayrollLineBreakdown(o.lineBreakdown),
      groupByEmployee: o.groupByEmployee !== false,
    };
  } catch {
    return defaultPayrollFilters();
  }
}

export function uniqueLineCategories(preview: PayrollRunPreviewDto | null): string[] {
  if (!preview) return [];
  const set = new Set<string>();
  for (const e of preview.employees) {
    for (const l of reportablePayrollLines(e.lines || [])) {
      if (l.category) set.add(l.category);
    }
  }
  return Array.from(set).sort((a, b) =>
    displayLineCategory(a).localeCompare(displayLineCategory(b))
  );
}
